/**
 * Weather Center (WEB-7144): the pure parts — units, the location's own clock, the hourly and
 * daily rows, the sun and moon calendar, and HuntWind stand status.
 *
 * The API always answers in imperial units (°F, mph, inches, inHg plus pressureMb) and the
 * client converts. Times are shown in the forecast location's time, not the browser's: the
 * observation's timeStamp carries the location's UTC offset, and hourly periods (which come back
 * in UTC) are shifted by it.
 */

export const num = v => (typeof v === 'number' && Number.isFinite(v) ? v : null)

const COMPASS_16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December']

export const normalizeDegrees = deg => ((deg % 360) + 360) % 360

export const compass16 = deg => (num(deg) == null ? null : COMPASS_16[Math.round(normalizeDegrees(deg) / 22.5) % 16])

// ---- Units ------------------------------------------------------------------------------

const metric = units => units === 'metric'

export const tempValue = (f, units) => (num(f) == null ? null : Math.round(metric(units) ? (f - 32) / 1.8 : f))
export const tempUnit = units => (metric(units) ? '°C' : '°F')
export const speedValue = (mph, units) => (num(mph) == null ? null : Math.round(metric(units) ? mph * 1.609344 : mph))
export const speedUnit = units => (metric(units) ? 'km/h' : 'mph')

// "0.12"" or "3.0 mm"; null when unknown.
export const precipAmount = (inches, units) => {
  if (num(inches) == null) return null
  return metric(units) ? `${(inches * 25.4).toFixed(1)} mm` : `${inches.toFixed(2)}"`
}

// Elevation/Get answers in meters.
export const elevationLabel = (meters, units) => {
  if (num(meters) == null) return null
  return metric(units)
    ? `${Math.round(meters).toLocaleString('en-US')} m`
    : `${Math.round(meters * 3.28084).toLocaleString('en-US')} ft`
}

const INHG_TO_MB = 33.8639

export const pressureValue = (obs, barometer) => {
  if (!obs) return null
  if (barometer === 'mBar') {
    const mb = num(obs.pressureMb) != null ? obs.pressureMb : num(obs.pressureIn) != null ? obs.pressureIn * INHG_TO_MB : null
    return mb == null ? null : String(Math.round(mb))
  }
  const inHg = num(obs.pressureIn) != null ? obs.pressureIn : num(obs.pressureMb) != null ? obs.pressureMb / INHG_TO_MB : null
  return inHg == null ? null : inHg.toFixed(2)
}

// Pressure trend between two readings in inHg: 'rising' | 'falling' | 'steady' | null.
export const pressureTrend = (previousIn, currentIn) => {
  if (num(previousIn) == null || num(currentIn) == null) return null
  const delta = currentIn - previousIn
  if (Math.abs(delta) < 0.005) return 'steady'
  return delta > 0 ? 'rising' : 'falling'
}

// ---- The location's clock ---------------------------------------------------------------

// Minutes east of UTC from an ISO string's own suffix ("…-06:00" → -360, "…Z" → 0), else null.
export const isoOffsetMinutes = iso => {
  const m = /(Z|([+-])(\d{2}):?(\d{2}))$/.exec(String(iso || '').trim())
  if (!m) return null
  if (m[1] === 'Z') return 0
  return (m[2] === '-' ? -1 : 1) * (Number(m[3]) * 60 + Number(m[4]))
}

// The location's offset: the first of these ISO strings that carries one. An offset-less
// string is not evidence; fall back to the browser's own offset.
export const locationOffset = (...isos) => {
  for (const iso of isos) {
    const offset = isoOffsetMinutes(iso)
    if (offset != null && /T\d{2}:\d{2}/.test(String(iso))) return offset
  }
  return -new Date().getTimezoneOffset()
}

// Wall-clock parts of an ISO instant at `offset` minutes east of UTC. A string with no zone is
// already wall-clock time and is read as such.
export const localParts = (iso, offset) => {
  const s = String(iso || '')
  const hasZone = isoOffsetMinutes(s) != null
  const t = Date.parse(hasZone ? s : `${s.replace(/Z?$/, '')}Z`)
  if (!Number.isFinite(t)) return null
  const d = new Date(hasZone ? t + (offset || 0) * 60000 : t)
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
    weekday: d.getUTCDay(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes()
  }
}

const pad2 = n => String(n).padStart(2, '0')

export const dateKey = (iso, offset) => {
  const p = localParts(iso, offset)
  return p ? `${p.year}-${pad2(p.month + 1)}-${pad2(p.day)}` : null
}

// "6:58 AM", in the time the string itself carries when `offset` is omitted.
export const clockLabel = (iso, offset) => {
  const p = localParts(iso, offset != null ? offset : isoOffsetMinutes(iso))
  if (!p) return null
  return `${p.hour % 12 === 0 ? 12 : p.hour % 12}:${pad2(p.minute)} ${p.hour < 12 ? 'AM' : 'PM'}`
}

export const hourLabel = (iso, offset) => {
  const p = localParts(iso, offset)
  if (!p) return null
  if (p.hour === 0) return '12 AM'
  if (p.hour === 12) return 'Noon'
  return p.hour < 12 ? `${p.hour} AM` : `${p.hour - 12} PM`
}

// "Wednesday, Oct 28"
export const dayTitle = (iso, offset) => {
  const p = localParts(iso, offset)
  return p ? `${DAYS[p.weekday]}, ${MONTHS[p.month].slice(0, 3)} ${p.day}` : ''
}

// "UTC−6", "UTC+5:30"
export const offsetLabel = offset => {
  if (num(offset) == null) return ''
  const sign = offset < 0 ? '−' : '+'
  const abs = Math.abs(offset)
  return `UTC${sign}${Math.floor(abs / 60)}${abs % 60 ? `:${pad2(abs % 60)}` : ''}`
}

export const monthLabel = (year, month) => `${MONTHS[month]} ${year}`

// A name for a point from Google's reverse geocode results (all of them: in the backcountry the
// first is a bare plus code). A town the point is in reads "Jennings, KS"; a town that only
// its postal code or plus code names is the nearby mailing town, so it reads "Near Wilsall, MT";
// otherwise the county, then the state or country. null when Google knows nothing.
const APPROXIMATE = ['plus_code', 'postal_code']

const componentOf = (result, type) => (result.address_components || []).find(c => (c.types || []).includes(type))

export const placeLabel = results => {
  const list = Array.isArray(results) ? results : []
  const first = type => list.map(r => componentOf(r, type)).find(Boolean)
  const state = first('administrative_area_level_1'),
    country = first('country'),
    region = state ? state.short_name : country ? country.long_name : null,
    withRegion = name => (region ? `${name}, ${region}` : name)

  const inTown = list.find(r => !(r.types || []).some(t => APPROXIMATE.includes(t)) && componentOf(r, 'locality'))
  if (inTown) return withRegion(componentOf(inTown, 'locality').long_name)
  const nearTown = first('locality')
  if (nearTown) return withRegion(`Near ${nearTown.long_name}`)
  const county = first('administrative_area_level_2')
  if (county) return withRegion(county.long_name)
  return state ? state.long_name : country ? country.long_name : null
}

// ---- Rows -------------------------------------------------------------------------------

export const iconUrl = obs => (obs && obs.baseIconUrl && obs.weatherIcon ? `${obs.baseIconUrl}${obs.weatherIcon}` : null)

// Hourly periods → a day header before each new local date, then one row per hour.
export const hourlyRows = (periods, offset) => {
  const rows = []
  let lastKey = null
  let previousIn = null
  const list = periods || []
  list.forEach((p, i) => {
    const key = dateKey(p.timeStamp, offset)
    if (!key) return
    if (key !== lastKey) {
      rows.push({ type: 'day', key: `day-${key}`, title: dayTitle(p.timeStamp, offset) })
      lastKey = key
    }
    rows.push({
      type: 'hour',
      key: `hour-${i}`,
      time: hourLabel(p.timeStamp, offset),
      period: p,
      trend: pressureTrend(previousIn, p.pressureIn)
    })
    previousIn = num(p.pressureIn)
  })
  return rows
}

// Daily periods, labelled in the location's time. After mid-afternoon The Weather Company drops
// today's day part, so today's missing values fall back to the current observation.
export const dailyRows = (periods, offset, observation) => (periods || []).map((p, i) => {
  const today = i === 0 && observation ? observation : {}
  const pick = field => (p[field] != null ? p[field] : today[field] != null ? today[field] : null)
  const parts = localParts(p.timeStamp, offset)
  return {
    key: `daily-${i}`,
    name: i === 0 ? 'Today' : parts ? DAYS[parts.weekday].slice(0, 3) : '',
    date: parts ? `${MONTHS[parts.month].slice(0, 3)} ${parts.day}` : '',
    icon: iconUrl(p) || iconUrl(today),
    summary: p.weatherPrimary || p.weather || today.weatherPrimary || null,
    maxTempF: pick('maxTempF'),
    minTempF: pick('minTempF'),
    precipPercent: pick('precipPercent'),
    windDirDeg: pick('windDirDeg'),
    windSpeedMph: pick('windSpeedMph')
  }
})

// "10h 21m" between two ISO instants.
export const daylightLabel = (riseIso, setIso) => {
  const rise = Date.parse(riseIso || ''), set = Date.parse(setIso || '')
  if (!Number.isFinite(rise) || !Number.isFinite(set) || set <= rise) return null
  const minutes = Math.round((set - rise) / 60000)
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

// ---- Sun and moon -----------------------------------------------------------------------

// Celestial/Get rows keyed by their local date. Timestamp is a local wall-clock time.
export const celestialByDate = celestial => {
  const out = {}
  const rows = (celestial && celestial.celestialData) || []
  rows.forEach(row => {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(row.timestamp || row.timeStamp || ''))
    if (m) out[m[1]] = row
  })
  return out
}

// moonPhasePercent is the position in the cycle (0 new → 0.5 full → 1 new).
export const moonWaxing = row => (row && num(row.moonPhasePercent) != null ? row.moonPhasePercent < 0.5 : null)

// "75%↑", or "Full" / "New" at the ends.
export const moonCellLabel = row => {
  const illum = row ? num(row.moonPhaseIllumination) : null
  if (illum == null) return ''
  if (illum >= 99) return 'Full'
  if (illum <= 1) return 'New'
  const waxing = moonWaxing(row)
  return `${illum}%${waxing == null ? '' : waxing ? '↑' : '↓'}`
}

// The lit part of a 24×24 moon of radius 10, as an SVG path (northern-hemisphere view).
export const moonPath = (illumPercent, waxing) => {
  const f = Math.max(0, Math.min(1, (num(illumPercent) || 0) / 100))
  const rx = Math.max(0.01, Math.abs(1 - 2 * f) * 10).toFixed(2)
  return waxing !== false
    ? `M12 2 A10 10 0 0 1 12 22 A${rx} 10 0 0 ${f < 0.5 ? 0 : 1} 12 2 Z`
    : `M12 2 A10 10 0 0 0 12 22 A${rx} 10 0 0 ${f < 0.5 ? 1 : 0} 12 2 Z`
}

// A month grid: blanks up to the first weekday, then one cell per day.
export const calendarCells = (year, month, byDate, todayKey) => {
  const firstWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay()
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const cells = []
  for (let i = 0; i < firstWeekday; i++) cells.push({ key: `blank-${i}`, blank: true })
  for (let day = 1; day <= daysInMonth; day++) {
    const key = `${year}-${pad2(month + 1)}-${pad2(day)}`
    cells.push({ key, day, isToday: key === todayKey, data: (byDate && byDate[key]) || null })
  }
  return cells
}

export const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

// ---- HuntWind ---------------------------------------------------------------------------

export const isHuntStand = marker => !!(marker && marker.markerType && marker.markerType.usageFlag == 3)

export const preferredWinds = marker => {
  try {
    const data = typeof marker.extendedData === 'string' ? JSON.parse(marker.extendedData || '{}') : marker.extendedData
    const winds = data && data.huntStandData && data.huntStandData.preferredWindDirections
    return Array.isArray(winds) ? winds.map(w => String(w).toLowerCase()) : []
  } catch (e) {
    return []
  }
}

// Android's WindDirectionIconsUtil.isWindDirectionInInterval: a preferred point covers the
// 45° sector centred on it (wind coming from that direction).
const SECTORS = { n: null, ne: [1, 3], e: [3, 5], se: [5, 7], s: [7, 9], sw: [9, 11], w: [11, 13], nw: [13, 15] }

export const inPreferredSector = (preferred, direction) => preferred.some(p => {
  if (p === 'n') return (direction >= 15 * 22.5 + 0.25 && direction <= 360) || (direction >= 0 && direction <= 22.5)
  const sector = SECTORS[p]
  return !!sector && direction >= sector[0] * 22.5 + 0.25 && direction <= sector[1] * 22.5
})

// Android's WindDirectionIconsUtil.getWindStatus: ideal when the wind and ±11.25° either side
// are all in a preferred sector, risky when only some are, bad when none are.
export const windStatus = (direction, preferred) => {
  if (num(direction) == null || !preferred || preferred.length === 0) return 'bad'
  const middle = normalizeDegrees(direction)
  const left = normalizeDegrees(middle + 11.25)
  const right = normalizeDegrees(middle - 11.25)
  const inMiddle = inPreferredSector(preferred, middle)
  const inLeft = inPreferredSector(preferred, left)
  const inRight = inPreferredSector(preferred, right)
  if (inMiddle) return inLeft && inRight ? 'ideal' : 'risky'
  return inLeft || inRight ? 'risky' : 'bad'
}

export const distanceMiles = (a, b) => {
  const r = Math.PI / 180
  const dLat = (b.lat - a.lat) * r
  const dLng = (b.lng - a.lng) * r
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2
  return 3958.8 * 2 * Math.asin(Math.sqrt(h))
}

export const distanceLabel = (miles, units) => {
  const value = metric(units) ? miles * 1.609344 : miles
  return `${value < 10 ? value.toFixed(1) : Math.round(value).toLocaleString('en-US')} ${metric(units) ? 'km' : 'mi'}`
}

// Stands nearest `origin` first, each with its wind direction from `windFor(stand)`; grouped
// ideal / risky (bad winds are left out, as on mobile). Stands with no wind yet are skipped.
export const rankStands = (stands, origin, windFor) => {
  const ranked = (stands || [])
    .filter(s => num(Number(s.latitude)) != null && num(Number(s.longitude)) != null)
    .map(stand => {
      const wind = windFor(stand)
      const direction = wind ? num(wind.direction) : null
      return {
        stand,
        wind,
        miles: distanceMiles(origin, { lat: Number(stand.latitude), lng: Number(stand.longitude) }),
        status: direction == null ? null : windStatus(direction, preferredWinds(stand))
      }
    })
    .sort((a, b) => a.miles - b.miles)
  return {
    ideal: ranked.filter(r => r.status === 'ideal'),
    risky: ranked.filter(r => r.status === 'risky'),
    bad: ranked.filter(r => r.status === 'bad').length,
    pending: ranked.filter(r => r.status === null).length
  }
}
